<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <b-card v-else>
            <h5 class="mb-0"><i class="fa fa-fw fa-money mr-2" />Cashback Penjualan</h5>
            <hr />
            <b-row>
                <b-col md="3">
                    <b-form-group label="Periode">
                        <b-form-input id="filter-period" type="month" v-model="filter.period" @change="getItems" />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Threshold (Min. Net Clear)">
                        <div class="h5 mb-0 pt-1">
                            <strong v-if="threshold != null">Rp. {{ $helper.formatThousand(threshold) }}</strong>
                            <span v-else class="text-muted">Belum diset</span>
                        </div>
                    </b-form-group>
                </b-col>
                <b-col md="6">
                    <b-form-group label="Aksi">
                        <b-button variant="primary" :disabled="isBusy" @click="getItems">
                            <i class="fa fa-search mr-2" />Cari
                        </b-button>
                        <b-button v-if="canEditThreshold" variant="outline-primary" class="ml-2" @click="openThreshold">
                            <i class="fa fa-sliders mr-2" />Atur Threshold
                        </b-button>
                        <b-button v-if="canView" variant="dark" class="ml-2" @click="downloadExcel">
                            <i class="fa fa-download mr-2" />Download Excel
                        </b-button>
                        <b-button v-if="canSendNow" variant="dark" class="ml-2" @click="openUpload">
                            <i class="fa fa-upload mr-2" />Upload Excel
                        </b-button>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-alert :show="!isBusy && cutoff != null && !isClosed" variant="info">
                Periode belum ditutup. Cashback bisa dikirim setelah {{ cutoffText }}.
            </b-alert>

            <div v-if="canSendNow" class="d-flex align-items-center mb-2">
                <b-button size="sm" variant="primary" class="mr-2" :disabled="isBusy || !selectable.length" @click="selected = selectable.map(i => i.customer_id)">
                    Pilih Semua
                </b-button>
                <b-button size="sm" variant="secondary" class="mr-3" :disabled="!selected.length" @click="selected = []">
                    Batal Pilih
                </b-button>
                <strong class="mr-3">{{ selected.length }} dipilih</strong>
                <b-button size="sm" variant="success" :disabled="!selected.length" @click="openBulk">
                    <i class="fa fa-send mr-2" />Kirim Terpilih
                </b-button>
            </div>

            <b-table id="cashback-table" bordered hover show-empty responsive class="my-3"
                :busy="isBusy"
                :items="items"
                :fields="fields"
            >
                <template v-slot:empty>
                    Tidak ada customer yang memenuhi syarat cashback pada periode ini.
                </template>
                <template v-slot:cell(select)="row">
                    <b-form-checkbox v-if="isSelectable(row.item)" v-model="selected" :value="row.item.customer_id" />
                </template>
                <template v-slot:cell(total_sales)="row">Rp. {{ $helper.formatThousand(row.item.total_sales) }}</template>
                <template v-slot:cell(total_return)="row">Rp. {{ $helper.formatThousand(row.item.total_return) }}</template>
                <template v-slot:cell(net)="row">Rp. {{ $helper.formatThousand(row.item.net) }}</template>
                <template v-slot:cell(status)="row">
                    <b-badge :variant="badge(row.item.status).variant">{{ badge(row.item.status).text }}</b-badge>
                    <div v-if="row.item.is_manual" class="small text-muted mt-1">Cashback ditransfer secara manual</div>
                </template>
                <template v-slot:cell(amount)="row">
                    <span v-if="row.item.amount != null">Rp. {{ $helper.formatThousand(row.item.amount) }}</span>
                    <span v-else>-</span>
                </template>
                <template v-slot:cell(sent_at)="row">
                    <span v-if="row.item.sent_at">
                        {{ $helper.parseDate(row.item.sent_at) }} {{ $helper.parseHourMinute(row.item.sent_at) }}
                    </span>
                    <span v-else>-</span>
                </template>
                <template v-slot:cell(sent_by_name)="row">{{ row.item.sent_by_name || "-" }}</template>
                <template v-slot:cell(actions)="row">
                    <b-button v-if="isSelectable(row.item)" size="sm" variant="success" @click="openSingle(row.item)">
                        Kirim
                    </b-button>
                    <template v-if="row.item.status === 'sent' && !row.item.is_manual">
                        <b-button size="sm" variant="primary" :disabled="isSubmitting" @click="openEdit(row.item)">Edit</b-button>
                        <b-button size="sm" variant="danger" class="ml-1" :disabled="isSubmitting" @click="cancelCashback(row.item)">Batal</b-button>
                    </template>
                </template>
            </b-table>
        </b-card>

        <b-modal id="m-cashback-send" :title="modal.mode === 'edit' ? 'Edit Nominal Cashback' : 'Kirim Cashback'" size="lg" scrollable centered
            no-close-on-backdrop no-close-on-esc hide-footer @hide="onHide">
            <p class="text-muted">Periode {{ filter.period }}. Nominal cashback ditentukan manual.</p>
            <b-form-group v-for="r in modal.rows" :key="r.customer_id" :label="r.customer_name">
                <b-input-group prepend="Rp.">
                    <b-form-input v-model="r.amount" maxlength="20" autocomplete="off" @input="onAmountInput(r)" />
                </b-input-group>
            </b-form-group>
            <b-alert :show="sendErrors.length > 0" variant="danger">
                <ul class="mb-0">
                    <li v-for="(e, i) in sendErrors" :key="i">{{ e }}</li>
                </ul>
            </b-alert>
            <div class="text-right mt-3">
                <b-button variant="secondary" class="mr-2" :disabled="isSubmitting" @click="$bvModal.hide('m-cashback-send')">Tutup</b-button>
                <b-button variant="success" :disabled="isSubmitting || !amountsValid" @click="submitSend">
                    {{ isSubmitting ? "Memproses..." : (modal.mode === "edit" ? "Simpan" : "Kirim") }}
                </b-button>
            </div>
        </b-modal>

        <b-modal id="m-cashback-threshold" title="Atur Threshold Cashback" centered no-close-on-backdrop no-close-on-esc hide-footer @hide="onHide">
            <p class="text-muted">Berlaku hanya untuk periode {{ filter.period }}.</p>
            <p v-if="isClosed" class="text-warning">Periode ini sudah ditutup. Daftar customer yang belum dikirim akan berubah, cashback yang sudah terkirim tidak berubah.</p>
            <b-input-group prepend="Rp.">
                <b-form-input v-model="thresholdForm.amount" maxlength="20" autocomplete="off" @input="onAmountInput(thresholdForm)" />
            </b-input-group>
            <div class="text-right mt-3">
                <b-button variant="secondary" class="mr-2" :disabled="isSubmitting" @click="$bvModal.hide('m-cashback-threshold')">Tutup</b-button>
                <b-button variant="success" :disabled="isSubmitting || !($helper.toNumber(thresholdForm.amount) > 0)" @click="submitThreshold">
                    {{ isSubmitting ? "Memproses..." : "Simpan" }}
                </b-button>
            </div>
        </b-modal>

        <b-modal id="m-cashback-upload" title="Upload Excel Cashback" centered no-close-on-backdrop no-close-on-esc hide-footer @hide="onHide">
            <b-form-file v-model="fileUpload" :state="Boolean(fileUpload)" placeholder="Pilih file excel" drop-placeholder="Tarik dan lepas file disini" accept=".xlsx, .xls, .csv" />
            <b-alert :show="importErrors.length > 0" variant="danger" class="mt-3">
                <ul class="mb-0">
                    <li v-for="(e, i) in importErrors" :key="i">{{ e }}</li>
                </ul>
            </b-alert>
            <b-button variant="primary" class="mt-2" :disabled="!fileUpload || isSubmitting" @click="importExcel">
                <i class="fa fa-upload mr-2" v-if="!isSubmitting" />
                {{ isSubmitting ? "Mengupload..." : "Unggah" }}
            </b-button>
        </b-modal>

        <b-modal id="m-cashback-download" title="Download Excel Cashback" centered no-close-on-backdrop no-close-on-esc hide-footer>
            <div v-if="status == 0">Menyiapkan file Excel, mohon menunggu...</div>
            <div v-else-if="status == -1">Gagal menyiapkan file Excel.</div>
            <div v-else>
                File Excel sudah siap. Tekan tombol dibawah ini untuk download file Excel.
                <div class="mt-2">
                    <a :href="path" download>
                        <b-button variant="primary"><i class="fa fa-download mr-2" />Download Excel Cashback</b-button>
                    </a>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { getCashbackStatusBadge, canSend } from "@/shared/cashback";

const FILTER_KEY = "listCashbackFilter";

function toPeriod(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function defaultPeriod() {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return toPeriod(d);
}

export default {
    data() {
        return {
            initStatus: 0,
            isBusy: true,
            isSubmitting: false,

            filter: { period: defaultPeriod() },

            items: [],
            isClosed: false,
            cutoff: null,
            threshold: null,
            thresholdForm: { amount: "" },
            selected: [],

            modal: { mode: "send", rows: [] },
            sendErrors: [],

            fileUpload: null,
            importErrors: [],

            status: 0,
            path: ""
        };
    },
    computed: {
        canView() {
            return this.$helper.isAllowed("229");
        },
        canManage() {
            return this.$helper.isAllowed("230");
        },
        canSendNow() {
            return this.canManage && this.isClosed && this.threshold != null;
        },
        canEditThreshold() {
            // Any period, closed or future. cutoff is null after a failed load.
            return this.$helper.isAllowed("231") && !this.isBusy && this.cutoff != null;
        },
        selectable() {
            return this.items.filter(this.isSelectable);
        },
        amountsValid() {
            return this.modal.rows.every(r => this.$helper.toNumber(r.amount) > 0);
        },
        cutoffText() {
            const c = this.cutoff;
            if (!c) return "-";
            return `${c.slice(8, 10)}-${c.slice(5, 7)}-${c.slice(0, 4)} ${c.slice(11, 16)}`;
        },
        fields() {
            const fields = [
                { key: "customer_name", label: "Customer" },
                { key: "total_sales", label: "Total Penjualan" },
                { key: "total_return", label: "Total Retur" },
                { key: "net", label: "Net Clear" },
                { key: "status", label: "Status" },
                { key: "amount", label: "Nominal Cashback" },
                { key: "sent_at", label: "Tgl Kirim" },
                { key: "sent_by_name", label: "Pengirim" }
            ];
            if (this.canSendNow) fields.unshift({ key: "select", label: "" });
            if (this.canManage) fields.push({ key: "actions", label: "Aksi" });
            return fields;
        }
    },
    methods: {
        init() {
            this.initStatus = 0;
            this.getItems().then(() => (this.initStatus = 1));
        },

        badge: getCashbackStatusBadge,

        isSelectable(item) {
            return this.canSendNow && canSend(item.status);
        },

        getPeriod() {
            const [year, month] = this.filter.period.split("-").map(Number);
            return { month, year };
        },

        getItems() {
            if (!/^\d{4}-\d{2}$/.test(this.filter.period)) return Promise.resolve();

            this.isBusy = true;
            this.selected = [];
            return this.$axios
                .get("cashback", { params: this.getPeriod() })
                .then(res => {
                    const data = res.data.data;
                    this.items = data.items;
                    this.isClosed = data.is_closed;
                    this.cutoff = data.cutoff;
                    this.threshold = data.threshold;
                    sessionStorage.setItem(FILTER_KEY, JSON.stringify(this.filter));
                })
                .catch(error => {
                    this.items = [];
                    this.cutoff = this.threshold = null;
                    this.$helper.parseError(this, error);
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },

        customerName(id) {
            if (id == null) return "";
            const item = this.items.find(i => i.customer_id === id);
            return item ? item.customer_name : `Customer #${id}`;
        },

        // Returns a list of messages for a 422 with per-row errors, else null.
        rowErrors(error) {
            const res = error.response;
            const list = res && res.status == 422 && res.data.errors;
            if (!Array.isArray(list) || !list.length) return null;
            return list.map(e => {
                const who = e.row != null ? `Baris ${e.row}` : this.customerName(e.customer_id);
                return who ? `${who}: ${e.message}` : e.message;
            });
        },

        // Block closing while a request is in flight.
        onHide(event) {
            if (this.isSubmitting) event.preventDefault();
        },

        onAmountInput(r) {
            const digits = String(r.amount).replace(/\D/g, "");
            this.$nextTick(() => (r.amount = digits === "" ? "" : this.$helper.formatThousand(Number(digits))));
        },

        openModal(mode, rows) {
            this.modal = { mode, rows };
            this.sendErrors = [];
            this.$bvModal.show("m-cashback-send");
        },

        toRow(item, amount = "") {
            return { customer_id: item.customer_id, cashback_id: item.cashback_id, customer_name: item.customer_name, amount };
        },

        openSingle(item) {
            this.openModal("send", [this.toRow(item)]);
        },

        openBulk() {
            this.openModal("send", this.items.filter(i => this.selected.includes(i.customer_id)).map(i => this.toRow(i)));
        },

        openEdit(item) {
            this.openModal("edit", [this.toRow(item, this.$helper.formatThousand(item.amount))]);
        },

        async submitSend() {
            this.isSubmitting = true;
            this.sendErrors = [];
            try {
                if (this.modal.mode === "edit") {
                    const r = this.modal.rows[0];
                    await this.$axios.patch(`cashback/${r.cashback_id}`, { amount: this.$helper.toNumber(r.amount) });
                    this.$helper.toastSuccess(this, "Nominal cashback berhasil diubah");
                } else {
                    const res = await this.$axios.post("cashback/send", {
                        ...this.getPeriod(),
                        items: this.modal.rows.map(r => ({ customer_id: r.customer_id, amount: this.$helper.toNumber(r.amount) }))
                    });
                    this.$helper.toastSuccess(this, `${res.data.data.sent} cashback berhasil dikirim`);
                }
                this.isSubmitting = false;
                this.$bvModal.hide("m-cashback-send");
                this.getItems();
            } catch (error) {
                const errors = this.rowErrors(error);
                if (errors) this.sendErrors = errors;
                else this.$helper.parseError(this, error);
            } finally {
                this.isSubmitting = false;
            }
        },

        cancelCashback(item) {
            const msg = `Batalkan cashback Rp. ${this.$helper.formatThousand(item.amount)} untuk ${item.customer_name}?`;
            if (!confirm(msg)) return;

            this.isSubmitting = true;
            this.$axios
                .post(`cashback/${item.cashback_id}/cancel`)
                .then(() => {
                    this.$helper.toastSuccess(this, "Cashback berhasil dibatalkan");
                    this.getItems();
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => (this.isSubmitting = false));
        },

        openThreshold() {
            this.thresholdForm.amount = this.threshold != null ?this.$helper.formatThousand(this.threshold) : "";
            this.$bvModal.show("m-cashback-threshold");
        },

        submitThreshold() {
            this.isSubmitting = true;
            this.$axios
                .put("cashback/threshold", { ...this.getPeriod(), threshold: this.$helper.toNumber(this.thresholdForm.amount) })
                .then(() => {
                    this.$helper.toastSuccess(this, "Threshold cashback berhasil diubah");
                    this.isSubmitting = false;
                    this.$bvModal.hide("m-cashback-threshold");
                    this.getItems();
                })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => (this.isSubmitting = false));
        },

        downloadExcel() {
            this.$bvModal.show("m-cashback-download");
            this.status = 0;
            this.$axios
                .post("cashback/export", this.getPeriod())
                .then(res => {
                    this.status = 1;
                    this.path = res.data.data.path;
                })
                .catch(error => {
                    this.status = -1;
                    this.$helper.parseError(this, error);
                });
        },

        openUpload() {
            this.fileUpload = null;
            this.importErrors = [];
            this.$bvModal.show("m-cashback-upload");
        },

        importExcel() {
            this.isSubmitting = true;
            this.importErrors = [];
            const { month, year } = this.getPeriod();
            const formData = new FormData();
            formData.append("file", this.fileUpload);
            formData.append("month", month);
            formData.append("year", year);
            this.$axios
                .post("cashback/import", formData, { headers: { "Content-Type": "multipart/form-data" } })
                .then(res => {
                    const { sent, skipped } = res.data.data;
                    this.$helper.toastSuccess(this, `Terkirim ${sent}, dilewati ${skipped}`);
                    this.isSubmitting = false;
                    this.$bvModal.hide("m-cashback-upload");
                    this.getItems();
                })
                .catch(error => {
                    const errors = this.rowErrors(error);
                    if (errors) this.importErrors = errors;
                    else this.$helper.parseError(this, error);
                })
                .finally(() => (this.isSubmitting = false));
        }
    },
    created() {
        const saved = JSON.parse(sessionStorage.getItem(FILTER_KEY) || "null");
        if (saved && /^\d{4}-\d{2}$/.test(saved.period)) this.filter = saved;
        this.init();
    }
};
</script>
